// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AccessibilityCommands from '@app/features/accessibility/commands/AccessibilityCommands';
import Accessibility from '@app/features/accessibility/state/Accessibility';
import AccessibilityOverride, {type AnimationOverrides} from '@app/features/accessibility/state/AccessibilityOverride';
import {PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {Switch} from '@app/features/ui/components/form/Switch';
import type {RadioOption} from '@app/features/ui/radio_group/RadioGroup';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import * as UserSettingsCommands from '@app/features/user/commands/UserSettingsCommands';
import styles from '@app/features/user/components/modals/tabs/accessibility_tab/AnimationTab.module.css';
import UserSettings from '@app/features/user/state/UserSettings';
import {StickerAnimationOptions} from '@app/vendor/constants/UserConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';
export const AnimationTabContent: React.FC = observer(() => {
	const {t} = useLingui();
	const mobileLayout = MobileLayout;
	const mobileStickerAnimationOverridden = Accessibility.mobileStickerAnimationOverridden;
	const mobileGifAutoPlayOverridden = Accessibility.mobileGifAutoPlayOverridden;
	const stickerAnimationOptions = useMemo(
		() =>
			[
				{value: StickerAnimationOptions.ALWAYS_ANIMATE, name: t`Always Animate`, desc: t`Stickers will always animate`},
				{
					value: StickerAnimationOptions.ANIMATE_ON_INTERACTION,
					name: t`Animate on Interaction`,
					desc: mobileLayout.enabled
						? t`Stickers will animate when you press them`
						: t`Stickers will animate when you hover or interact with them`,
				},
				{value: StickerAnimationOptions.NEVER_ANIMATE, name: t`Never Animate`, desc: t`Stickers will never animate`},
			] as ReadonlyArray<RadioOption<number>>,
		[mobileLayout.enabled, t],
	);
	const isOverridden = (setting: 'gif_auto_play' | 'animate_emoji' | 'animate_stickers'): boolean => {
		if (mobileLayout.enabled) return false;
		return AccessibilityOverride.isOverriddenByReducedMotion(setting);
	};
	const getOverrideDescription = (
		setting: 'gif_auto_play' | 'animate_emoji' | 'animate_stickers',
	): string | undefined => {
		if (isOverridden(setting)) {
			return t`This setting is currently overridden by your reduced motion preferences`;
		}
		return;
	};
	const handleAnimationSettingChange = (
		setting: 'gif_auto_play' | 'animate_emoji' | 'animate_stickers',
		_value: unknown,
		updateAction: () => void,
	) => {
		const dirtyKey: keyof AnimationOverrides =
			setting === 'gif_auto_play'
				? 'gifAutoPlayDirty'
				: setting === 'animate_emoji'
					? 'animateEmojiDirty'
					: 'animateStickersDirty';
		AccessibilityOverride.markDirty(dirtyKey);
		updateAction();
	};
	return (
		<>
			<Switch
				label={t`Play Animated Emojis`}
				description={getOverrideDescription('animate_emoji')}
				value={
					isOverridden('animate_emoji') ? AccessibilityOverride.effectiveAnimateEmoji : UserSettings.getAnimateEmoji()
				}
				disabled={isOverridden('animate_emoji')}
				onChange={(value) => {
					if (mobileLayout.enabled) {
						AccessibilityCommands.update({
							mobileAnimateEmojiOverridden: true,
							mobileAnimateEmojiValue: value,
						});
					} else {
						handleAnimationSettingChange('animate_emoji', value, () =>
							UserSettingsCommands.update({animateEmoji: value}),
						);
					}
				}}
			/>
			<Switch
				label={
					mobileLayout.enabled ? t`Automatically play GIFs` : t`Automatically play GIFs when ${PRODUCT_NAME} is focused`
				}
				description={
					mobileLayout.enabled && !mobileGifAutoPlayOverridden
						? t`Defaults to off on mobile to preserve battery life and data usage.`
						: getOverrideDescription('gif_auto_play')
				}
				value={
					isOverridden('gif_auto_play') ? AccessibilityOverride.effectiveGifAutoPlay : UserSettings.getGifAutoPlay()
				}
				disabled={isOverridden('gif_auto_play')}
				onChange={(value) => {
					if (mobileLayout.enabled) {
						AccessibilityCommands.update({
							mobileGifAutoPlayOverridden: true,
							mobileGifAutoPlayValue: value,
						});
					} else {
						handleAnimationSettingChange('gif_auto_play', value, () =>
							UserSettingsCommands.update({gifAutoPlay: value}),
						);
					}
				}}
			/>
			<div className={styles.radioSection}>
				<div className={styles.radioHeader}>
					<div className={styles.radioLabel}>
						<Trans>Sticker animations</Trans>
					</div>
					{mobileLayout.enabled && !mobileStickerAnimationOverridden ? (
						<p className={styles.radioDescription}>
							{t`Defaults to animate on interaction on mobile to preserve battery life.`}
						</p>
					) : (
						getOverrideDescription('animate_stickers') && (
							<p className={styles.radioDescription}>{getOverrideDescription('animate_stickers')}</p>
						)
					)}
				</div>
				<RadioGroup
					aria-label={t`Sticker animation preference`}
					options={stickerAnimationOptions}
					value={
						isOverridden('animate_stickers')
							? AccessibilityOverride.effectiveAnimateStickers
							: UserSettings.getAnimateStickers()
					}
					disabled={isOverridden('animate_stickers')}
					onChange={(value) => {
						if (mobileLayout.enabled) {
							AccessibilityCommands.update({
								mobileStickerAnimationOverridden: true,
								mobileStickerAnimationValue: value,
							});
						} else {
							handleAnimationSettingChange('animate_stickers', value, () =>
								UserSettingsCommands.update({animateStickers: value}),
							);
						}
					}}
				/>
			</div>
		</>
	);
});
